import type { ComponentProjectMetricsResponse, DashboardMetric } from '../../../../types/dashboard';
import { MAX_QUARTERS_CHART } from '../../constants';
import { readPointFieldGoal } from '../../chartGoalUtils';
import { parseQuarter } from '../../metricSeriesUtils';

export type AswpProgramId = 'aiMinor' | 'tCap' | 'tcc' | 't3';

export type AswpProgramSnapshot = {
  participation: number;
  placement: number;
  placementPct: number | null;
};

export type AswpProgramParticipationPoint = {
  quarter: string;
  programs: Record<AswpProgramId, AswpProgramSnapshot>;
  totals: AswpProgramSnapshot;
  goalTargets?: {
    programs: Record<
      AswpProgramId,
      {
        participation?: number;
        placement?: number;
        placementRate?: number;
      }
    >;
    totals?: {
      participation?: number;
      placement?: number;
      placementRate?: number;
      participationAllTime?: number;
    };
  };
};

export type AswpInsightsPoint = {
  quarter: string;
  insightsGenerated: number;
  goalTarget?: number;
};

export type AswpInsightsData = {
  series: AswpInsightsPoint[];
  allTime: number;
};

export type AswpComponentProjectData = {
  series: AswpProgramParticipationPoint[];
  allTime: {
    programs: Record<AswpProgramId, AswpProgramSnapshot>;
    totals: AswpProgramSnapshot;
  };
  insights: AswpInsightsData | null;
};

export const ASWP_PROGRAM_LABELS: Record<AswpProgramId, string> = {
  aiMinor: 'AI Minor',
  tCap: 'T-CAP',
  tcc: 'TCC Cert',
  t3: 'T3',
};

export const ASWP_PROGRAM_ORDER: AswpProgramId[] = ['aiMinor', 'tCap', 'tcc', 't3'];

/** Grant target: 50% placement by end of Year 5 (aswp-milestones.json). */
export const ASWP_PLACEMENT_RATE_GOAL_PCT = 50;

/** Grant target: 400–500+ participants across all programs (aswp-milestones.json). */
export const ASWP_TOTAL_PARTICIPATION_ALL_TIME_GOAL = 400;

const PLACEMENT_RATE_GOAL_KEYS = [
  'placement_rate',
  'placementRate',
  'participant_placement_rate',
  'participantPlacementRate',
  'program_placement_rate',
  'programPlacementRate',
];

const PARTICIPATION_ALL_TIME_GOAL_KEYS = [
  'participation_all_time',
  'participationAllTime',
  'program_participation_all_time',
  'programParticipationAllTime',
  'total_program_participants',
  'totalProgramParticipants',
];

const SERIES_META_KEYS = new Set(['quarter', 'qoq', 'yoy', 'goal', 'goalStatus', 'value']);

type ProgramMetricType = 'participation' | 'placement';

type ProgramFieldConfig = {
  program: AswpProgramId;
  type: ProgramMetricType;
  keys: string[];
  metricIdPatterns: string[];
};

const PROGRAM_FIELD_CONFIG: ProgramFieldConfig[] = [
  {
    program: 'aiMinor',
    type: 'participation',
    keys: ['ai_minor_program_participation', 'aiMinorProgramParticipation'],
    metricIdPatterns: ['ai_minor', 'program_participation'],
  },
  {
    program: 'aiMinor',
    type: 'placement',
    keys: ['ai_minor_participant_placement', 'aiMinorParticipantPlacement'],
    metricIdPatterns: ['ai_minor', 'participant_placement'],
  },
  {
    program: 'tCap',
    type: 'participation',
    keys: ['t_cap_program_participation', 'tCapProgramParticipation'],
    metricIdPatterns: ['t_cap', 'program_participation'],
  },
  {
    program: 'tCap',
    type: 'placement',
    keys: ['t_cap_participant_placement', 'tCapParticipantPlacement'],
    metricIdPatterns: ['t_cap', 'participant_placement'],
  },
  {
    program: 'tcc',
    type: 'participation',
    keys: [
      'stackable_credential_micro_pathway_program_participation',
      'stackableCredentialMicroPathwayProgramParticipation',
    ],
    metricIdPatterns: ['stackable_credential', 'program_participation'],
  },
  {
    program: 'tcc',
    type: 'placement',
    keys: [
      'stackable_credential_micro_pathway_participant_placement',
      'stackableCredentialMicroPathwayParticipantPlacement',
    ],
    metricIdPatterns: ['stackable_credential', 'participant_placement'],
  },
  {
    program: 't3',
    type: 'participation',
    keys: [
      't3_program_participation',
      't3ProgramParticipation',
      't3_program_program_participation',
      't3ProgramProgramParticipation',
    ],
    metricIdPatterns: ['t3', 'program_participation'],
  },
  {
    program: 't3',
    type: 'placement',
    keys: ['t3_program_participant_placement', 't3ProgramParticipantPlacement'],
    metricIdPatterns: ['t3', 'participant_placement'],
  },
];

const INSIGHTS_KEYS = new Set(['insights_generated', 'insightsGenerated']);

function normalizeFieldKey(key: string): string {
  return key.toLowerCase().replace(/[_\s-]/g, '');
}

function quarterSortKey(label: string): number {
  const parsed = parseQuarter(label);
  if (!parsed) return 0;
  return parsed.year * 10 + parsed.quarter;
}

function readNumericField(point: Record<string, unknown>, keys: string[]): number {
  for (const key of keys) {
    const numeric = asNumeric(point[key]);
    if (numeric !== null) return numeric;
  }
  return 0;
}

function inferProgramFieldFromKey(
  key: string
): { program: AswpProgramId; type: ProgramMetricType; storageKey: string } | null {
  const normalized = normalizeFieldKey(key);
  for (const config of PROGRAM_FIELD_CONFIG) {
    for (const candidate of config.keys) {
      if (normalizeFieldKey(candidate) === normalized) {
        return { program: config.program, type: config.type, storageKey: config.keys[0] };
      }
    }
  }

  const fuzzyRules: Array<{ program: AswpProgramId; type: ProgramMetricType; patterns: string[] }> = [
    { program: 'aiMinor', type: 'participation', patterns: ['aiminor', 'programparticipation'] },
    { program: 'aiMinor', type: 'placement', patterns: ['aiminor', 'participantplacement'] },
    { program: 'tCap', type: 'participation', patterns: ['tcap', 'programparticipation'] },
    { program: 'tCap', type: 'placement', patterns: ['tcap', 'participantplacement'] },
    {
      program: 'tcc',
      type: 'participation',
      patterns: ['stackablecredential', 'programparticipation'],
    },
    {
      program: 'tcc',
      type: 'placement',
      patterns: ['stackablecredential', 'participantplacement'],
    },
    { program: 't3', type: 'participation', patterns: ['t3', 'programparticipation'] },
    { program: 't3', type: 'placement', patterns: ['t3', 'participantplacement'] },
  ];

  for (const rule of fuzzyRules) {
    if (rule.patterns.every((pattern) => normalized.includes(pattern))) {
      const config = PROGRAM_FIELD_CONFIG.find(
        (entry) => entry.program === rule.program && entry.type === rule.type
      );
      if (config) {
        return { program: rule.program, type: rule.type, storageKey: config.keys[0] };
      }
    }
  }

  return null;
}

function inferProgramMetric(
  metricId: string
): { program: AswpProgramId; type: ProgramMetricType; keys: string[] } | null {
  const id = normalizeFieldKey(metricId);
  for (const config of PROGRAM_FIELD_CONFIG) {
    const [first, second] = config.metricIdPatterns;
    if (id.includes(normalizeFieldKey(first)) && id.includes(normalizeFieldKey(second))) {
      return { program: config.program, type: config.type, keys: config.keys };
    }
  }

  const metricKey = metricId.includes('.') ? metricId.split('.').pop() ?? metricId : metricId;
  const fromKey = inferProgramFieldFromKey(metricKey);
  if (fromKey) {
    const config = PROGRAM_FIELD_CONFIG.find(
      (entry) => entry.program === fromKey.program && entry.type === fromKey.type
    );
    if (config) {
      return { program: config.program, type: config.type, keys: config.keys };
    }
  }

  return null;
}

function isInsightsKey(key: string): boolean {
  if (INSIGHTS_KEYS.has(key)) return true;
  const normalized = normalizeFieldKey(key);
  return normalized.includes('insightsgenerated');
}

function isInsightsMetricId(metricId: string): boolean {
  const id = normalizeFieldKey(metricId);
  return id.includes('insightsgenerated') || id === 'insights' || id.endsWith('insights');
}

function readInsightsFromRecord(record: Record<string, unknown>): number {
  let total = 0;
  for (const [key, value] of Object.entries(record)) {
    if (!isInsightsKey(key)) continue;
    const numeric = asNumeric(value);
    if (numeric !== null) total += numeric;
  }
  return total;
}

function isProgramKpisMetricId(metricId: string): boolean {
  const id = metricId.toLowerCase();
  return id.includes('programkpi') || id.includes('program_kpi');
}

function asNumeric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function mergeNumericFields(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): void {
  for (const [key, value] of Object.entries(source)) {
    if (SERIES_META_KEYS.has(key)) continue;
    const numeric = asNumeric(value);
    if (numeric === null) continue;

    const field = inferProgramFieldFromKey(key);
    if (field) {
      target[field.storageKey] = numeric;
      continue;
    }
    if (hasProgramParticipationOrPlacementKey(key)) {
      target[key] = numeric;
    }
    if (!INSIGHTS_KEYS.has(key) && !normalizeFieldKey(key).includes('insights')) {
      target[key] = numeric;
    }
  }
}

function emptyProgramSnapshot(): AswpProgramSnapshot {
  return { participation: 0, placement: 0, placementPct: null };
}

function emptyPrograms(): Record<AswpProgramId, AswpProgramSnapshot> {
  return {
    aiMinor: emptyProgramSnapshot(),
    tCap: emptyProgramSnapshot(),
    tcc: emptyProgramSnapshot(),
    t3: emptyProgramSnapshot(),
  };
}

export function computePlacementPct(participation: number, placement: number): number | null {
  if (participation <= 0) return null;
  return (placement / participation) * 100;
}

function toProgramSnapshot(
  raw: Record<string, unknown>,
  program: AswpProgramId
): AswpProgramSnapshot {
  const participationConfig = PROGRAM_FIELD_CONFIG.find(
    (entry) => entry.program === program && entry.type === 'participation'
  );
  const placementConfig = PROGRAM_FIELD_CONFIG.find(
    (entry) => entry.program === program && entry.type === 'placement'
  );
  const participation = readNumericField(raw, participationConfig?.keys ?? []);
  const placement = readNumericField(raw, placementConfig?.keys ?? []);
  return {
    participation,
    placement,
    placementPct: computePlacementPct(participation, placement),
  };
}

function sumPrograms(programs: Record<AswpProgramId, AswpProgramSnapshot>): AswpProgramSnapshot {
  const participation = ASWP_PROGRAM_ORDER.reduce((sum, id) => sum + programs[id].participation, 0);
  const placement = ASWP_PROGRAM_ORDER.reduce((sum, id) => sum + programs[id].placement, 0);
  return {
    participation,
    placement,
    placementPct: computePlacementPct(participation, placement),
  };
}

function mergePointGoals(
  merged: Record<string, unknown>,
  point: Record<string, unknown>
): void {
  if (!point.goal || typeof point.goal !== 'object') return;
  const existing =
    merged.goal && typeof merged.goal === 'object'
      ? (merged.goal as Record<string, unknown>)
      : {};
  merged.goal = { ...existing, ...(point.goal as Record<string, unknown>) };
}

function readPlacementRateGoal(raw: Record<string, unknown>): number {
  return readPointFieldGoal(raw, PLACEMENT_RATE_GOAL_KEYS) ?? ASWP_PLACEMENT_RATE_GOAL_PCT;
}

function readParticipationAllTimeGoal(raw: Record<string, unknown>, fallback: number): number {
  return readPointFieldGoal(raw, PARTICIPATION_ALL_TIME_GOAL_KEYS) ?? fallback;
}

export function formatAswpParticipationAllTimeGoalLabel(goal: number): string {
  if (goal === ASWP_TOTAL_PARTICIPATION_ALL_TIME_GOAL) {
    return '400+';
  }
  return String(goal);
}

function isPlacementRateMetricId(metricId: string): boolean {
  const id = normalizeFieldKey(metricId);
  return id.includes('placementrate') || (id.includes('placement') && id.includes('rate'));
}

function resolveMetricGoalFieldKeys(metric: DashboardMetric): string[] | null {
  const inferred = inferProgramMetric(metric.metricId);
  if (inferred) return inferred.keys;
  if (isInsightsMetricId(metric.metricId)) {
    return ['insights_generated', 'insightsGenerated'];
  }
  if (isPlacementRateMetricId(metric.metricId)) {
    return PLACEMENT_RATE_GOAL_KEYS;
  }
  return null;
}

function assignGoalTarget(
  existingGoal: Record<string, unknown>,
  fieldKeys: string[],
  goalEval: { operator?: string; target: number }
): void {
  for (const key of fieldKeys) {
    if (!existingGoal[key]) {
      existingGoal[key] = goalEval;
    }
  }
}

/** Per-metric API goals often use `value`; map to participation/placement/insights field keys. */
function promoteInferredMetricGoal(
  merged: Record<string, unknown>,
  metric: DashboardMetric
): void {
  const goal = merged.goal;
  if (!goal || typeof goal !== 'object') return;

  const goalRecord = goal as Record<string, unknown>;
  const valueGoal = goalRecord.value;
  if (!valueGoal || typeof valueGoal !== 'object') return;

  const resolvedKeys = resolveMetricGoalFieldKeys(metric);
  if (!resolvedKeys) return;

  const next = { ...goalRecord };
  let changed = false;
  for (const key of resolvedKeys) {
    if (!next[key]) {
      next[key] = valueGoal;
      changed = true;
    }
  }
  if (changed) {
    merged.goal = next;
  }
}

function applyMetricLevelGoals(
  merged: Record<string, unknown>,
  quarter: string,
  metric: DashboardMetric
): void {
  const activeGoals = (metric as DashboardMetric & { goals?: { active?: Array<{
    grain?: string;
    periodKey?: string;
    targets?: Record<string, { operator?: string; value?: number }>;
  }> } }).goals?.active;
  if (!Array.isArray(activeGoals) || activeGoals.length === 0) return;

  const meta = parseQuarter(quarter);
  if (!meta) return;

  const existingGoal =
    merged.goal && typeof merged.goal === 'object'
      ? ({ ...(merged.goal as Record<string, unknown>) } as Record<string, unknown>)
      : {};

  const resolvedKeys = resolveMetricGoalFieldKeys(metric);

  for (const goal of activeGoals) {
    const isAnnual = goal.grain === 'annual' && goal.periodKey === String(meta.year);
    const isQuarterly =
      goal.grain === 'quarter' && goal.periodKey === `${meta.year}-Q${meta.quarter}`;
    if (!isAnnual && !isQuarterly) continue;

    for (const [fieldName, target] of Object.entries(goal.targets ?? {})) {
      if (existingGoal[fieldName]) continue;
      if (typeof target.value !== 'number' || !Number.isFinite(target.value)) continue;

      const goalEval = {
        operator: target.operator,
        target: target.value,
      };

      if (fieldName === 'value' && resolvedKeys) {
        assignGoalTarget(existingGoal, resolvedKeys, goalEval);
        continue;
      }

      existingGoal[fieldName] = goalEval;
    }
  }

  if (Object.keys(existingGoal).length > 0) {
    merged.goal = existingGoal;
  }
}

function toParticipationPoint(quarter: string, raw: Record<string, unknown>): AswpProgramParticipationPoint {
  const programs = emptyPrograms();
  const programGoals: Record<
    AswpProgramId,
    { participation?: number; placement?: number; placementRate?: number }
  > = {
    aiMinor: {},
    tCap: {},
    tcc: {},
    t3: {},
  };

  const placementRateGoal = readPlacementRateGoal(raw);

  for (const program of ASWP_PROGRAM_ORDER) {
    programs[program] = toProgramSnapshot(raw, program);
    const participationConfig = PROGRAM_FIELD_CONFIG.find(
      (entry) => entry.program === program && entry.type === 'participation'
    );
    const placementConfig = PROGRAM_FIELD_CONFIG.find(
      (entry) => entry.program === program && entry.type === 'placement'
    );
    const participationGoal = readPointFieldGoal(raw, participationConfig?.keys ?? []);
    const placementGoal = readPointFieldGoal(raw, placementConfig?.keys ?? []);
    if (participationGoal !== null) {
      programGoals[program].participation = participationGoal;
    }
    if (placementGoal !== null) {
      programGoals[program].placement = placementGoal;
    }
    programGoals[program].placementRate = placementRateGoal;
  }

  const totals = sumPrograms(programs);
  const totalParticipationAllTimeGoal = readParticipationAllTimeGoal(
    raw,
    ASWP_TOTAL_PARTICIPATION_ALL_TIME_GOAL
  );
  const totalParticipationGoal = ASWP_PROGRAM_ORDER.reduce<number | undefined>((sum, program) => {
    const value = programGoals[program].participation;
    if (value === undefined) return sum;
    return (sum ?? 0) + value;
  }, undefined);
  const totalPlacementGoal = ASWP_PROGRAM_ORDER.reduce<number | undefined>((sum, program) => {
    const value = programGoals[program].placement;
    if (value === undefined) return sum;
    return (sum ?? 0) + value;
  }, undefined);

  return {
    quarter,
    programs,
    totals,
    goalTargets: {
      programs: programGoals,
      totals: {
        ...(totalParticipationGoal !== undefined ? { participation: totalParticipationGoal } : {}),
        ...(totalPlacementGoal !== undefined ? { placement: totalPlacementGoal } : {}),
        placementRate: placementRateGoal,
        participationAllTime: totalParticipationAllTimeGoal,
      },
    },
  };
}

function extractNumericKeys(series: Record<string, unknown>[]): string[] {
  const keys = new Set<string>();
  for (const point of series) {
    for (const [key, value] of Object.entries(point)) {
      if (!SERIES_META_KEYS.has(key) && asNumeric(value) !== null) {
        keys.add(key);
      }
    }
  }
  return Array.from(keys);
}

function hasProgramParticipationOrPlacementKey(key: string): boolean {
  const normalized = normalizeFieldKey(key);
  return normalized.includes('programparticipation') || normalized.includes('participantplacement');
}

function metricHasProgramFields(metric: DashboardMetric): boolean {
  if (inferProgramMetric(metric.metricId)) return true;
  const keys = extractNumericKeys(metric.series);
  if (keys.some((key) => inferProgramFieldFromKey(key) !== null || hasProgramParticipationOrPlacementKey(key))) {
    return true;
  }
  if (metric.allTime) {
    return Object.keys(metric.allTime).some(
      (key) => inferProgramFieldFromKey(key) !== null || hasProgramParticipationOrPlacementKey(key)
    );
  }
  return false;
}

export function isAswpInsightsMetric(metricId: string, metric?: DashboardMetric): boolean {
  if (isInsightsMetricId(metricId)) return true;
  if (!metric) return false;
  const keys = extractNumericKeys(metric.series);
  return keys.some(isInsightsKey) && !metricHasProgramFields(metric);
}

export function isAswpProgramMetric(metricId: string, metric?: DashboardMetric): boolean {
  if (isProgramKpisMetricId(metricId)) return true;
  if (inferProgramMetric(metricId)) return true;
  if (metric && metricHasProgramFields(metric)) return true;
  return false;
}

export function mapAswpComponentProjectData(
  data: ComponentProjectMetricsResponse | undefined
): AswpComponentProjectData | null {
  if (!data?.sections?.length) return null;

  const byQuarter = new Map<string, Record<string, unknown>>();
  const allTimeRaw: Record<string, unknown> = {};
  const insightsByQuarter = new Map<string, number>();
  const insightsGoalsByQuarter = new Map<string, number>();
  let insightsAllTime = 0;

  for (const section of data.sections) {
    for (const metric of section.metrics) {
      const inferredFromId = inferProgramMetric(metric.metricId);
      const insightsMetric = isInsightsMetricId(metric.metricId);

      for (const point of metric.series) {
        const quarter = String(point.quarter ?? '');
        if (!quarter) continue;

        const merged = byQuarter.get(quarter) ?? { quarter };
        mergeNumericFields(merged, point);

        if (inferredFromId) {
          const numeric = asNumeric(point.value);
          if (numeric !== null) {
            merged[inferredFromId.keys[0]] = numeric;
          }
        }

        const quarterInsights = readInsightsFromRecord(point);
        if (insightsMetric) {
          const valueInsights = asNumeric(point.value);
          if (valueInsights !== null) {
            insightsByQuarter.set(quarter, (insightsByQuarter.get(quarter) ?? 0) + valueInsights);
          }
        }
        if (quarterInsights > 0) {
          insightsByQuarter.set(quarter, (insightsByQuarter.get(quarter) ?? 0) + quarterInsights);
        }

        mergePointGoals(merged, point);
        applyMetricLevelGoals(merged, quarter, metric);
        promoteInferredMetricGoal(merged, metric);
        byQuarter.set(quarter, merged);

        if (insightsMetric || quarterInsights > 0) {
          const insightsGoal = readPointFieldGoal(merged, [
            'insights_generated',
            'insightsGenerated',
            'value',
          ]);
          if (insightsGoal !== null) {
            insightsGoalsByQuarter.set(quarter, insightsGoal);
          }
        }
      }

      if (metric.allTime) {
        mergeNumericFields(allTimeRaw, metric.allTime);
        if (inferredFromId) {
          const numeric = asNumeric(metric.allTime.value);
          if (numeric !== null) {
            allTimeRaw[inferredFromId.keys[0]] = numeric;
          }
        }

        const allTimeInsights = readInsightsFromRecord(metric.allTime);
        if (insightsMetric) {
          const valueInsights = asNumeric(metric.allTime.value);
          if (valueInsights !== null) {
            insightsAllTime += valueInsights;
          }
        }
        if (allTimeInsights > 0) {
          insightsAllTime += allTimeInsights;
        }
      }
    }
  }

  const series = Array.from(byQuarter.entries())
    .sort(([a], [b]) => quarterSortKey(a) - quarterSortKey(b))
    .slice(-MAX_QUARTERS_CHART)
    .map(([quarter, raw]) => toParticipationPoint(quarter, raw));

  const insightsSeries = Array.from(insightsByQuarter.entries())
    .sort(([a], [b]) => quarterSortKey(a) - quarterSortKey(b))
    .slice(-MAX_QUARTERS_CHART)
    .map(([quarter, insightsGenerated]) => ({
      quarter,
      insightsGenerated,
      ...(insightsGoalsByQuarter.has(quarter)
        ? { goalTarget: insightsGoalsByQuarter.get(quarter) }
        : {}),
    }));

  const insights: AswpInsightsData | null =
    insightsSeries.length > 0 || insightsAllTime > 0
      ? { series: insightsSeries, allTime: insightsAllTime }
      : null;

  if (series.length === 0 && insights === null) return null;

  const allTimePrograms = emptyPrograms();
  for (const program of ASWP_PROGRAM_ORDER) {
    allTimePrograms[program] = toProgramSnapshot(allTimeRaw, program);
  }

  const participationSeries =
    series.length > 0
      ? series
      : insightsSeries.map((point) => ({
          quarter: point.quarter,
          programs: emptyPrograms(),
          totals: emptyProgramSnapshot(),
        }));

  return {
    series: participationSeries,
    allTime: {
      programs: allTimePrograms,
      totals: sumPrograms(allTimePrograms),
    },
    insights,
  };
}

export function hasAswpInsightsData(data: AswpComponentProjectData): boolean {
  if (!data.insights) return false;
  return (
    data.insights.allTime > 0 ||
    data.insights.series.some((point) => point.insightsGenerated > 0)
  );
}

export function hasAswpProgramParticipationData(data: AswpComponentProjectData): boolean {
  if (data.series.length === 0) return false;

  const hasSeriesValues = data.series.some(
    (point) =>
      point.totals.participation > 0 ||
      point.totals.placement > 0 ||
      ASWP_PROGRAM_ORDER.some(
        (program) => point.programs[program].participation > 0 || point.programs[program].placement > 0
      )
  );

  const hasAllTimeValues =
    data.allTime.totals.participation > 0 ||
    data.allTime.totals.placement > 0 ||
    ASWP_PROGRAM_ORDER.some(
      (program) =>
        data.allTime.programs[program].participation > 0 ||
        data.allTime.programs[program].placement > 0
    );

  return hasSeriesValues || hasAllTimeValues;
}
